import type { SelectOption } from '@aus-platform/design-system';
import {
  Button,
  CheckBox,
  CheckboxAlignment,
  FormMessage,
  FormMessageVariant,
  IconIdentifier,
  Input,
  InputGroup,
  toast,
} from '@aus-platform/design-system';
import { useAppSelector } from 'app/hooks';
import { filter, isEmpty, isNil, snakeCase, without, xor } from 'lodash';
import { PolygonEventType } from 'map-3d/shared';
import {
  selectActiveWorkspaceLayersByFeatureType,
  selectMap3dDataset,
  selectMap3DState,
  selectMap3dWorkspace,
  setCurrentActiveMapTool,
} from 'map-3d/shared/map-3d-slices';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { FeatureResponseData, SmartDetectCreatePayload } from 'shared/api';
import {
  FeatureGeometryType,
  FeatureType,
  handleResponseErrorMessage,
  handleResponseMessage,
  LayerType,
  useAccessTagList,
  useFeatureList,
} from 'shared/api';
import { useAddSmartDetectRequest } from 'shared/api/workspace';
import { GlobalContext } from 'shared/context';
import { useInputFields } from 'shared/hooks';
import { areAccessTagsRequiredForLayer } from 'src/sites/helpers';
import { MapTool } from '../../../map-3d-container/map-3d-tools';
import { smartDetectFeatureFlags } from '../../constants';
import type { WorkspaceLayer, WorkspaceLayerListObj } from '../../shared';
import { MarkArea, MarkAreaToggle } from '../../shared';
import { setVectorLayerVisibility } from '../map-3d-workspace';
import {
  FormErrorMessages,
  SmartDetectOutput,
  SmartDetectOutputLayerPrefixNameMapping,
  SmartDetectOutputMapping,
} from './enums';
import type { SmartDetectFormValues } from './types';

const smartDetectOutputs: SmartDetectOutput[] = [
  SmartDetectOutput.BenchToeCrest,
  SmartDetectOutput.BuildingFootprintsWithRoofTypes,
  SmartDetectOutput.DrainageAnalysis,
  SmartDetectOutput.HeapDetection,
  SmartDetectOutput.PondsAndLakes,
  SmartDetectOutput.RiversAndStreams,
  SmartDetectOutput.TreeCanopies,
  SmartDetectOutput.MetalledRoads,
  SmartDetectOutput.UnMetalledRoads,
  SmartDetectOutput.BoreholeDetection,
  SmartDetectOutput.FoundationPilingDetection,
  SmartDetectOutput.PurlinDetection,
  SmartDetectOutput.SolarPanelDetection,
  SmartDetectOutput.DrillHoleDetection,
  SmartDetectOutput.VegetationCoverDetection,
];

export const GenerateSmartDetectForm: React.FC<{
  setShowSmartDetectForm: (boolean) => void;
}> = ({ setShowSmartDetectForm }) => {
  // States.
  const [currentMarkedArea, setCurrentMarkedArea] = useState<MarkArea>(
    MarkArea.Draw,
  );
  const [isDrawPolygonToolEnabled, setIsDrawPolygonToolEnabled] =
    useState<boolean>(false);
  const [hasPolygons, setHasPolygons] = useState<boolean>(false);
  const [accessTagList, setAccessTagList] = useState<SelectOption[]>([]);

  // Selectors.
  const dispatch = useDispatch();
  const { aoiManager } = useAppSelector(selectMap3DState);
  const { workspaceLayers } = useAppSelector(selectMap3dWorkspace);
  const { selectedTerrainIteration } = useAppSelector(selectMap3dDataset);

  // Hooks.
  const { loggedUser } = useContext(GlobalContext);

  // Drawing tools.
  const polygonTool = aoiManager?.polygonTool;

  // Constants.
  const activeSmartDetectOutputs = smartDetectOutputs.filter(
    (output) => loggedUser?.featureFlags[smartDetectFeatureFlags[output]],
  );

  const initialSmartDetectFormValues: SmartDetectFormValues = {
    activeOrtho: null,
    selectedOutputs: [],
    smartAreaWkt: undefined,
    workspaceLayer: null,
    smartDetectOutputNames: Object.fromEntries(
      activeSmartDetectOutputs.map((output) => [
        output,
        SmartDetectOutputLayerPrefixNameMapping[output],
      ]),
    ),
    accessTags: [],
  };

  const {
    values,
    errors,
    names,
    onBlur,
    onFocus,
    setValues,
    setErrors,
    setOptional,
  } = useInputFields(initialSmartDetectFormValues);

  const { layerAccessControl: accessControl } =
    useAppSelector(selectMap3dWorkspace);

  const layerAccessTagsRequired = areAccessTagsRequiredForLayer(
    accessControl.accessType,
    loggedUser,
  );

  // APIs.
  const {
    data: featuresResponse,
    isFetching: isLoadingFeaturesResponse,
    isSuccess: isSuccessFeaturesResponse,
    isError: isErrorFeaturesResponse,
    error: featuresResponseError,
    refetch: refetchFeatures,
  } = useFeatureList(
    values.workspaceLayer?.value.id ?? null,
    FeatureType.Polygon,
    false,
  );

  const {
    mutate: sendSmartDetectRequest,
    isPending: isSmartDetectRequestLoading,
    data: smartDetectResponse,
    isSuccess: isSmartDetectRequestSuccess,
    isError: isSmartDetectRequestError,
    error: smartDetectError,
  } = useAddSmartDetectRequest();

  const {
    data: accessTagsListResponse,
    isSuccess: isAccessTagsListSuccess,
    isLoading: isAccessTagsListLoading,
    isError: isAccessTagsListError,
    error: accessTagsListError,
  } = useAccessTagList();

  const activeOrthoOptions = useMemo(() => {
    const orthoLayers = filter(workspaceLayers, {
      type: LayerType.Orthomosaic,
    })
      .sort((layer1, layer2) => (layer2.zIndex || 0) - (layer1.zIndex || 0))
      .map((layer) => ({ label: layer.name, value: layer.id }));

    setValues({ ...values, activeOrtho: orthoLayers[0] });
    return orthoLayers;
  }, [workspaceLayers]);

  const activeLayers = useAppSelector((state) =>
    selectActiveWorkspaceLayersByFeatureType(
      state,
      FeatureGeometryType.Polygon,
    ),
  );

  const polygonLayersSelectOption: SelectOption<WorkspaceLayer>[] =
    activeLayers.map((activeLayer) => ({
      label: activeLayer.name,
      value: activeLayer,
    }));

  const isParentCheckboxChecked = useMemo(
    () => values.selectedOutputs.length === activeSmartDetectOutputs.length,
    [values.selectedOutputs],
  );

  const isParentCheckboxIndeterminate = useMemo(
    () =>
      values.selectedOutputs.length > 0 &&
      values.selectedOutputs.length < activeSmartDetectOutputs.length,
    [values.selectedOutputs],
  );

  // useEffects.
  useEffect(() => {
    return () => {
      setVectorLayerVisibility(visibleWorkspaceLayers, true);
    };
  }, []);

  useEffect(() => {
    setOptional((prev) => ({
      ...prev,
      accessTags: !layerAccessTagsRequired,
    }));
  }, [layerAccessTagsRequired]);

  useEffect(() => {
    if (!polygonTool || !isDrawPolygonToolEnabled) {
      return;
    }

    const onPolygonCreated = () => {
      setHasPolygons(true);
    };

    polygonTool.addEventListener(
      PolygonEventType.PolygonCreated,
      onPolygonCreated,
    );

    return () => {
      polygonTool.removeEventListener(
        PolygonEventType.PolygonCreated,
        onPolygonCreated,
      );
    };
  }, [polygonTool, isDrawPolygonToolEnabled]);

  useEffect(() => {
    handleResponseErrorMessage(isErrorFeaturesResponse, featuresResponseError);
  }, [isErrorFeaturesResponse, featuresResponseError]);

  useEffect(() => {
    handleResponseMessage(
      isSmartDetectRequestSuccess,
      isSmartDetectRequestError,
      smartDetectResponse,
      smartDetectError,
    );
    if (isSmartDetectRequestSuccess) {
      setVectorLayerVisibility(visibleWorkspaceLayers, true);
      setShowSmartDetectForm(false);
    }
  }, [
    smartDetectResponse,
    isSmartDetectRequestSuccess,
    isSmartDetectRequestError,
  ]);

  useEffect(() => {
    if (isSuccessFeaturesResponse && !isNil(featuresResponse)) {
      loadPolygonsFromWorkspace(featuresResponse.data.features);
    }
  }, [isSuccessFeaturesResponse, featuresResponse]);

  // useEffect - Handle addition & deletion of polygons when selected layer is changed.
  useEffect(() => {
    if (isNil(values.workspaceLayer)) {
      return;
    }

    refetchFeatures();

    // Clear all drawn smart detect polygons.
    deleteSmartDetectPolygonsByMarkedArea(MarkArea.Draw);

    /**
     * Clear all polygons from previously selected layers
     * (excluding the current selected layers polygons)
     **/
    const features = values.workspaceLayer?.value.features;
    const selectedSmartDetectPolygonIds: Array<string> | undefined =
      features?.map((feature) => {
        return feature.id + 'smartDetect';
      });
    deleteSmartDetectPolygonsByMarkedArea(
      MarkArea.LayerFromWorkspace,
      selectedSmartDetectPolygonIds,
    );
  }, [values.workspaceLayer]);

  useEffect(() => {
    if (isAccessTagsListSuccess && accessTagsListResponse) {
      const accessTags = accessTagsListResponse.data.list.map((accessTag) => {
        return {
          label: accessTag.name,
          value: accessTag.id,
        };
      });
      setAccessTagList(accessTags);
    }

    handleResponseErrorMessage(isAccessTagsListError, accessTagsListError);
  }, [accessTagsListResponse, accessTagsListError]);

  // useMemo.
  const visibleWorkspaceLayers: WorkspaceLayerListObj = useMemo(
    () =>
      Object.keys(workspaceLayers).reduce(
        (toggledOnLayersListObject, layerId) => {
          if (workspaceLayers[layerId].show) {
            toggledOnLayersListObject[layerId] = workspaceLayers[layerId];
            return toggledOnLayersListObject;
          }
          return toggledOnLayersListObject;
        },
        {},
      ),
    [workspaceLayers],
  );

  // Handlers.
  // TODO: Create 2 separate functions for handling exit drawing mode and enter drawing mode.
  const startDrawingHandler = () => {
    // Drawing Mode.
    polygonTool?.activate({
      smartDetectPolygons: MarkArea.Draw,
    });
    dispatch(setCurrentActiveMapTool(MapTool.None));
    setIsDrawPolygonToolEnabled(true);
  };

  const endDrawingHandler = () => {
    polygonTool?.deactivate();
    setIsDrawPolygonToolEnabled(false);
  };

  const resetDrawingHandler = () => {
    polygonTool?.deleteAllCurrentPolygons();
    setHasPolygons(false);
  };

  const handleParentCheckboxClick = () => {
    const newSelectedOutputs = !isParentCheckboxChecked
      ? [...activeSmartDetectOutputs]
      : [];

    setValues({
      ...values,
      selectedOutputs: newSelectedOutputs,
      smartDetectOutputNames: Object.fromEntries(
        activeSmartDetectOutputs.map((output) => [
          output,
          newSelectedOutputs.includes(output)
            ? `${SmartDetectOutputLayerPrefixNameMapping[output]}_${values.activeOrtho?.label || ''}`
            : '',
        ]),
      ),
    });
  };

  const handleOutputSelect = (output: string) => {
    const newSelectedOutputs = values.selectedOutputs.includes(output)
      ? without(values.selectedOutputs, output)
      : [...values.selectedOutputs, output];

    setValues((prev) => ({
      ...prev,
      selectedOutputs: newSelectedOutputs,
      smartDetectOutputNames: {
        ...prev.smartDetectOutputNames,
        [output]: `${SmartDetectOutputLayerPrefixNameMapping[output]}_${values.activeOrtho?.label || ''}`,
      },
    }));
  };

  const handleOrthoSelect = (option: SelectOption | null) => {
    setValues((prev) => ({
      ...prev,
      activeOrtho: option,
      //Below, changing the default output names based on active ortho.
      smartDetectOutputNames: Object.fromEntries(
        activeSmartDetectOutputs.map((output) => [
          output,
          prev.selectedOutputs.includes(output)
            ? `${SmartDetectOutputLayerPrefixNameMapping[output]}_${option?.label || ''}`
            : '',
        ]),
      ) as Record<SmartDetectOutput, string>,
    }));
  };

  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const output = name.split('.')[1] as SmartDetectOutput;

    setValues({
      ...values,
      smartDetectOutputNames: {
        ...values.smartDetectOutputNames,
        [output]: value,
      },
    });

    if (isEmpty(value)) {
      setErrors({
        ...errors,
        smartDetectOutputNames: FormErrorMessages.LayerNameNotProvided,
      });
    } else {
      setErrors({
        ...errors,
        smartDetectOutputNames: '',
      });
    }
  };

  const deleteSmartDetectPolygonsByMarkedArea = (
    markArea: MarkArea,
    excludePolygons?: string[],
  ) => {
    const smartDetectPolygonsIds = polygonTool
      ?.getPolygonByProperty('smartDetectPolygons', markArea)
      .map((smartDetectPolygon) => {
        return smartDetectPolygon.id;
      });

    /**
     * XOR finds the symmetric difference.
     * Ref - https://en.wikipedia.org/wiki/Symmetric_difference
     * In this case, removes the excludePolygons from the smartDetectPolygonsIds.
     */
    const polygonsToDelete = xor(smartDetectPolygonsIds, excludePolygons);

    for (const polygon of polygonsToDelete) {
      polygonTool?.deleteById(polygon);
    }

    polygonTool?.resetCurrentDrawnArray(false);
  };

  const generateSmartDetect = () => {
    const payload: SmartDetectCreatePayload = {
      iteration: selectedTerrainIteration?.id || '',
      inputOrthoLayer: values.activeOrtho?.value || '',
      smartDetectOutputs: values.selectedOutputs.map((output) => ({
        outputType: SmartDetectOutputMapping[output],
        name: values.smartDetectOutputNames[output] as string,
      })),
      layerAccessTags:
        values.accessTags?.map((accessTag) => accessTag.value) ?? [],
    };

    if (polygonTool?.isAnyPolygonDrawn) {
      let wktString: string | null = null;
      const polygonIds = polygonTool.currentDrawnPolygonIds;
      if (polygonIds.length > 1) {
        toast.error('Please draw only one polygon.');
        return;
      }
      const polygon = polygonTool.currentDrawnPolygons[polygonIds[0]];
      if (isEmpty(polygonIds)) {
        toast.error('Please draw a polygon first.');
        return;
      }
      wktString = polygon.exportWKT().wkt;
      payload.smartAreaWkt = wktString;
    } else {
      setIsDrawPolygonToolEnabled(false);
    }

    if (values.workspaceLayer) {
      payload.inputAOILayer = values.workspaceLayer.value.id;
    }
    sendSmartDetectRequest(payload);
  };

  const isSubmitDisabled =
    !values.activeOrtho ||
    values.selectedOutputs.length === 0 ||
    errors.smartDetectOutputNames.length !== 0 ||
    (currentMarkedArea === MarkArea.Draw && !hasPolygons) ||
    (currentMarkedArea === MarkArea.LayerFromWorkspace &&
      !values.workspaceLayer) ||
    (layerAccessTagsRequired && isEmpty(values.accessTags));

  const isOutputSelectDisabled = () => {
    if (currentMarkedArea === MarkArea.Draw) {
      return !hasPolygons;
    } else {
      return !values.workspaceLayer;
    }
  };

  const onToggleChange = (toggledValue: string) => {
    // TODO: Refactor toggle switch to accept type to return type with onChange value.
    const toggleValueAsEnum = snakeCase(toggledValue) as MarkArea;

    if (toggleValueAsEnum === MarkArea.Draw) {
      setVectorLayerVisibility(visibleWorkspaceLayers, true);
      deleteSmartDetectPolygonsByMarkedArea(MarkArea.LayerFromWorkspace);
      setErrors({
        ...errors,
        workspaceLayer: '',
      });
      setValues({
        ...values,
        workspaceLayer: null,
      });
    } else {
      setIsDrawPolygonToolEnabled(false);
      setVectorLayerVisibility(visibleWorkspaceLayers, false);
      deleteSmartDetectPolygonsByMarkedArea(MarkArea.Draw);
      setHasPolygons(false);

      polygonTool?.deactivate();

      if (
        isNil(polygonLayersSelectOption) ||
        polygonLayersSelectOption.length === 0
      ) {
        setErrors({
          ...errors,
          workspaceLayer: FormErrorMessages.NoActiveWorkspaceLayer,
        });
      }
      setValues({ ...values });
    }

    setCurrentMarkedArea(toggleValueAsEnum);
  };

  // Helpers.
  // TODO: refactor this to be reusable in every select from layers modules.
  const loadPolygonsFromWorkspace = (
    featureResponse: FeatureResponseData[],
  ) => {
    for (const feature of featureResponse) {
      if (feature.type === FeatureType.Polygon) {
        polygonTool?.importWKT(
          feature.geometry,
          feature.id,
          {
            smartDetectPolygons: MarkArea.LayerFromWorkspace,
          },
          feature.name,
        );
      }
    }
  };

  return (
    <>
      <div className="smart-detect">
        <form className="smart-detect__form">
          <InputGroup>
            <Input.Label>Active Terrain</Input.Label>
            <Input.Text disabled value={selectedTerrainIteration?.name} />
          </InputGroup>
          <InputGroup>
            <Input.Label isRequired>Active Orthomosaic</Input.Label>
            <Input.Select
              options={activeOrthoOptions}
              value={values.activeOrtho}
              onChange={handleOrthoSelect}
              error={errors.activeOrtho}
              isClearable={false}
            />
          </InputGroup>
          <InputGroup>
            <Input.Label isRequired={layerAccessTagsRequired}>
              Access Tags
            </Input.Label>
            <Input.Select
              placeholder="Select Access Tags"
              options={accessTagList}
              isMulti={true}
              name={names.accessTags}
              value={values.accessTags}
              error={errors.accessTags}
              isLoading={isAccessTagsListLoading}
              onChange={(selectedAccessTags) => {
                setValues({ ...values, accessTags: selectedAccessTags });
              }}
              {...{ onBlur, onFocus }}
            />
          </InputGroup>
          <MarkAreaToggle
            currentMarkedArea={currentMarkedArea}
            onToggleChange={onToggleChange}
            drawingHandlers={{
              endDrawingHandler,
              startDrawingHandler,
              resetDrawingHandler,
            }}
            isDrawingToolEnabled={isDrawPolygonToolEnabled}
            workspaceLayerError={errors.workspaceLayer}
            selectLayerSelectProps={{
              options: polygonLayersSelectOption,
              value: values.smartAreaWkt,
              name: names.smartAreaWkt,
              isLoading: isLoadingFeaturesResponse,
              onChange: (selectedLayer: SelectOption<WorkspaceLayer>) => {
                setValues({ ...values, workspaceLayer: selectedLayer });
              },
              onBlur,
              onFocus,
            }}
            isRequired
          />

          <InputGroup className="smart-detect__form__select-output">
            <div className="smart-detect__form__select-output__parent-checkbox">
              <Input.Label isRequired>Select Outputs</Input.Label>
              <Input.CheckBox
                checked={isParentCheckboxChecked}
                indeterminate={isParentCheckboxIndeterminate}
                onClick={handleParentCheckboxClick}
                disabled={isOutputSelectDisabled()}
              />
            </div>
            <div className="smart-detect__form__select-output__smart-detect-list">
              {activeSmartDetectOutputs.map((output) => {
                const isChecked = values.selectedOutputs.includes(output);
                const outputName = values.smartDetectOutputNames[output];
                const showError = isChecked && !outputName;

                return (
                  <div
                    key={output}
                    className="smart-detect__form__select-output__smart-detect-list__item"
                  >
                    <CheckBox
                      title={output}
                      checked={isChecked}
                      alignCheckbox={CheckboxAlignment.Right}
                      onClick={() => handleOutputSelect(output)}
                      disabled={isOutputSelectDisabled()}
                      showCard
                    />
                    {isChecked && (
                      <>
                        <Input.Text
                          name={`smartDetectOutputNames.${output}`}
                          className="smart-detect__form__select-output__text"
                          value={outputName}
                          onChange={handleTextChange}
                          {...{ onBlur, onFocus }}
                        />
                        {showError && (
                          <FormMessage
                            className="smart-detect__form__select-output__text__form-message"
                            variant={FormMessageVariant.Error}
                            showIcon
                            iconIdentifier={IconIdentifier.ExclamationCircle}
                            message={FormErrorMessages.LayerNameNotProvided}
                          />
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </InputGroup>
        </form>
        <div className="smart-detect__footer">
          <Button
            onClick={generateSmartDetect}
            disabled={isSubmitDisabled}
            isLoading={isSmartDetectRequestLoading}
          >
            Start Detection
          </Button>
        </div>
      </div>
    </>
  );
};
